import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface ApprovalModeContextType {
  isApprovalModeEnabled: boolean;
  setApprovalModeEnabled: (enabled: boolean) => void;
}

const ApprovalModeContext = createContext<ApprovalModeContextType | undefined>(undefined);

export const ApprovalModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [isApprovalModeEnabled, setIsApprovalModeEnabledState] = useState<boolean>(false);
  // Track which team we're currently subscribed to so we can resubscribe on team switch
  const activeTeamIdRef = useRef<string | null>(null);

  // Re-fetch from DB whenever the authenticated user changes (including on page reload
  // once the Supabase session is restored from storage).
  useEffect(() => {
    if (!user?.id) return; // wait until auth is ready

    const fetchApprovalMode = async () => {
      try {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('active_team_id')
          .eq('id', user.id)
          .single();

        if (profileError || !profile?.active_team_id) {
          // TODO: GA4_TRACKING - No active team, using default approval mode (false)
          setIsApprovalModeEnabledState(false);
          return;
        }

        activeTeamIdRef.current = profile.active_team_id;

        const { data: team, error: teamError } = await supabase
          .from('teams')
          .select('approval_mode')
          .eq('id', profile.active_team_id)
          .single();

        if (teamError && teamError.code !== 'PGRST116') {
          console.error('[ApprovalModeContext] Error fetching approval mode:', teamError);
          setIsApprovalModeEnabledState(false);
        } else if (team) {
          // TODO: GA4_TRACKING - Loaded approval_mode from DB (team.approval_mode)
          setIsApprovalModeEnabledState(team.approval_mode ?? false);
        }
      } catch (err) {
        console.error('[ApprovalModeContext] Unexpected error:', err);
      }
    };

    fetchApprovalMode();
  }, [user?.id]);

  // Real-time subscription: only react to updates on the user's active team
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('approval-mode-team-watch')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'teams' },
        (payload) => {
          // Only apply if this is the team we care about
          if (
            activeTeamIdRef.current &&
            payload.new?.id === activeTeamIdRef.current &&
            typeof payload.new.approval_mode === 'boolean'
          ) {
            setIsApprovalModeEnabledState(payload.new.approval_mode);
          }
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [user?.id]);

  const setApprovalModeEnabled = (enabled: boolean) => {
    setIsApprovalModeEnabledState(enabled);
  };

  return (
    <ApprovalModeContext.Provider value={{ isApprovalModeEnabled, setApprovalModeEnabled }}>
      {children}
    </ApprovalModeContext.Provider>
  );
};

export const useApprovalMode = (): ApprovalModeContextType => {
  const context = useContext(ApprovalModeContext);
  if (context === undefined) {
    throw new Error('useApprovalMode must be used within an ApprovalModeProvider');
  }
  return context;
};
